import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import bem from '@justpark/helpers/src/bem/bem';
import LinkComponent from '@justpark/ui/src/components/Link';
import PoliciesWrapper from './PoliciesWrapper';
import OrderedList from './OrderedList';

const policiesClass = 'c-policies';
const TermsAndConditions = () => {
  const { i18n, t } = useTranslation();

  const locale = i18n.language;
  return (
    <PoliciesWrapper>
      {locale === 'cy' ? (
        <LinkComponent
          href="https://uploads.justpark.com/documents/cymraeg/telerau-ac-amodau.pdf"
          target="_blank"
          cypress="terms-conditions-cy"
        >
          {t(
            'termsAndConditions:link',
            'Terms and conditions (click to download PDF)'
          )}
        </LinkComponent>
      ) : (
        <>
          <p>
            If you are using JustPark’s mobile payment service, please note our
            terms relating to this service under our Mobile Payment Terms &amp;
            Conditions.
          </p>
          <p>
            If you are parking in a JustPark car park, please note our terms
            relating to this service under our Car Park Terms &amp; Conditions
          </p>
          <h3 className={bem(policiesClass, 'sub-heading')}>Introduction</h3>
          <OrderedList number margin>
            <li>
              The website justpark.com (&quot;the Site&quot;) is a parking space
              rental agency which allows you to book parking spaces anywhere in
              the world.
            </li>
            <li>
              The Site is operated by JustPark Limited (&quot;we&quot; or
              &quot;us&quot;) and membership is open to any individual who
              chooses to register with the site.
            </li>
            <li>
              These terms will apply to anyone who views or uses the Site
              (&quot;you&quot;).
            </li>
            <li>
              Whether you register or not you will be bound by these terms and
              conditions in full and you also agree to be bound by the terms of
              our{' '}
              <strong>
                <Link to="/privacy-policy/">Privacy policy</Link>
              </strong>{' '}
              which explains the use we are authorised to make of your personal
              information.
            </li>
            <li>
              These Terms and Conditions relate to your passive use of the Site,
              if you wish to book a parking space through the Site you will be
              bound by our{' '}
              <strong>
                <Link to="/driver-agreement/">Driver Agreement</Link>
              </strong>
              .
            </li>
            <li>
              If you are a parking space owner and you wish to rent your parking
              space with us you will be bound by our{' '}
              <strong>
                <Link to="/owner-agreement/">
                  Parking Space Owner agreement
                </Link>
              </strong>
              .
            </li>
            <li>
              Please note that these terms and conditions may be amended from
              time to time. Notification of any changes will be made by posting
              new terms onto the Site. In continuing to use the Site you confirm
              that you accept the then current terms and conditions in full at
              the time you use the Site.
            </li>
          </OrderedList>
          <h3 className={bem(policiesClass, 'sub-heading')}>Registration</h3>
          <OrderedList number margin>
            <li>
              Registration with the Site is currently free, however in order to
              become a registered user you will have to provide some basic
              personal information.
            </li>
            <li>
              You agree that all information supplied on registration is true
              and accurate and will be kept up to date at all times. We will use
              the information provided to us to contact you.
            </li>
            <li>
              You may not have more than one registration and we reserve the
              right at our discretion to delete or cancel the membership of any
              person who in our opinion possesses more than one registration at
              any time.
            </li>
            <li>
              You are not entitled to allow any other person to use your
              registration. You may not impersonate any other person in any
              registration whether or not that other person is a user of the
              Site.
            </li>
            <li>
              On receipt of an email by you to us cancelling your registration
              then we will terminate your registration.
            </li>
            <li>
              We reserve the right to terminate your registration immediately
              without notice if in our opinion you have breached these terms.
            </li>
          </OrderedList>
          <h3 className={bem(policiesClass, 'sub-heading')}>
            Use and Abuse of the Site
          </h3>
          <OrderedList margin number>
            <li>
              You agree that any information you supply will be true and
              accurate.
            </li>
            <li>
              You may not use the site to publish any offensive, inaccurate,
              misleading, defamatory, fraudulent, or illegal information or
              content.
            </li>
            <li>
              We reserve the right at our discretion to remove any content from
              the site, terminate your registration or membership and restrict
              your access to our services at any time for any reason.
            </li>
            <li>
              We grant you a limited licence to access and make personal use of
              the Site, but not to download (other than page caching) or modify
              it, or any portion of it, except with our express written consent.
            </li>
            <li>
              In the event that you are informed that you will no longer be
              entitled to access the services you will not be entitled to
              register again and you will no longer have permission to use the
              Site.
            </li>
          </OrderedList>
          <h3 className={bem(policiesClass, 'sub-heading')}>
            Advertising and Commercial Use
          </h3>
          <OrderedList number margin>
            <li>
              Users of the Site are not entitled to directly advertise to or
              solicit the custom of other users without our express written
              consent.
            </li>
            <li>
              You are not entitled to resell or commercially exploit the
              Site&apos;s contents other than content you have posted. You are
              not entitled to use any data mining, robots, or similar data
              gathering and extraction tools to collect usernames, email
              addresses or any other data for the purposes of sending
              unsolicited email or for any other use.
            </li>
            <li>
              In the event that you are found by us to have been sending
              unsolicited email to our users then we reserve the right to
              terminate without notice your use of the Site without limiting any
              other rights and remedies we may have.
            </li>
          </OrderedList>
          <h3 className={bem(policiesClass, 'sub-heading')}>Our Liability</h3>
          <OrderedList number margin>
            <li>
              The material displayed on our site is provided without any
              guarantees, conditions or warranties as to its accuracy and is
              provided on an &quot;as is&quot; basis. To the extent permitted by
              law, we hereby expressly exclude all conditions, warranties and
              other terms which might otherwise be implied by any law,
              regulation, statute, common law or law of equity.
            </li>
            <li>
              We will not be responsible for any breach of these Terms and
              Conditions caused by circumstances beyond our reasonable control.
            </li>
            <li>
              Although we aim to offer you the best service possible, we make no
              promise that the services at the Site will meet your requirements.
              We cannot guarantee that the services will be fault-free.
            </li>
            <li>
              Your access to the Site may be occasionally restricted to allow
              for repairs, maintenance or the introduction of new facilities or
              services. We will attempt to restore the service as soon as we
              reasonably can.
            </li>
            <li>
              From time to time it may be necessary to suspend access to the
              Site for a period of time and any such interruptions shall not
              constitute a breach by us of these terms.
            </li>
            <li>
              We will not be liable for any business, financial, or economic
              loss nor for any consequential or indirect loss (such as lost
              reputation, lost profit or lost opportunity) arising as a result
              of your use of the Site whether such loss is incurred or suffered
              as a result of our negligence or otherwise.
            </li>
          </OrderedList>
          <h3 className={bem(policiesClass, 'sub-heading')}>Communication</h3>
          <OrderedList number margin>
            <li>
              We request that all information provided to us is accurate,
              current and complete.
            </li>
            <li>
              All notices sent to you will be sent to the email address provided
              to us (as updated by you). By accepting these terms you give your
              consent to receive communications from us by email and you agree
              that all agreements, notices, disclosures and other communications
              that we provide to you by email satisfy any legal requirement that
              such communications be in writing.
            </li>
            <li>
              Personal information that you supply to us will not be disclosed
              by us to any third party save in accordance with our Privacy
              Policy. You agree that we may use the personal information
              supplied by you in accordance with our Privacy Policy.
            </li>
          </OrderedList>
          <h3 className={bem(policiesClass, 'sub-heading')}>
            Other Sites and Linking
          </h3>
          <OrderedList number margin>
            <li>
              The Site may include links to other web sites or material which
              are beyond its control. We are not responsible for content on any
              site outside the Site.
            </li>
            <li>
              You may link to our home page, provided you do so in a way that is
              fair and legal and does not damage our reputation or take
              advantage of it, but you must not establish a link in such a way
              as to suggest any form of association, approval or endorsement on
              our part where none exists.
            </li>
            <li>
              You must not establish a link from any website that is not owned
              by you.
            </li>
            <li>
              Our site must not be framed on any other site, nor may you create
              a link to any part of our site other than the home page. We
              reserve the right to withdraw linking permission without notice.
            </li>
          </OrderedList>

          <h3 className={bem(policiesClass, 'sub-heading')}>
            Intellectual Property
          </h3>
          <OrderedList number margin>
            <li>
              The format and content of this Site is protected by United Kingdom
              and international copyright and we reserve all rights in relation
              to our copyright whether owned or licensed to us and all rights
              are reserved to any of our registered and unregistered trademarks
              (whether owned or licensed to us) which appear on this Site.
            </li>
            <li>
              By displaying user-generated content on this site you expressly
              assign all copyright and other rights to such content to us (and
              you agree to waive all moral rights in relation to such content)
              for the avoidance of doubt we are permitted to use any
              user-generated content for any of our other business purposes,
              even following termination of your registration or membership.
            </li>
            <li>
              We do not screen user-generated content (including content
              relating to available parking spaces) or information on the Site
              and we cannot give any assurance as to its accuracy or
              completeness. Users of this Site are expressly asked not to
              publish any defamatory, misleading or offensive content or any
              content which infringes any other persons intellectual property
              rights (eg. copyright). Any such content is contrary to our policy
              we do not accept liability in respect of such content, and the
              user responsible will be personally liable for any damages or
              other liability arising and you agree to indemnify us in relation
              to any liability we may suffer as a result of any such content.
            </li>
            <li>
              This website or any portion of this website may not be reproduced,
              duplicated, copied, sold, resold, visited, or otherwise exploited
              for any commercial purpose without our express written consent.
              You may not systematically extract and/or re-utilise parts of the
              contents of the Site without our express written consent. In
              particular, you may not utilise any data mining, robots, or
              similar data gathering and extraction tools to extract (whether
              once or many times) for re-utilisation of any substantial parts of
              this Site without our express written consent.
            </li>
          </OrderedList>

          <h3 className={bem(policiesClass, 'sub-heading')}>
            Referral Programme
          </h3>
          <OrderedList number>
            <li>To be eligible for the Referral Reward Programme:</li>
            <OrderedList roman margin>
              <li>You must have an active User account on JustPark;</li>
              <li>You may not refer yourself for the Referral Program;</li>
              <li>
                The Referred User should not already have a User account (active
                or inactive) on JustPark;
              </li>
              <li>
                The Referred User should sign up for a User account using the
                activation link of the Referral Invitation;
              </li>
              <li>
                You won&apos;t receive rewards if you&apos;re the driver or
                property owner on a booking with a person you refer.
                Additionally, if both a driver and property owner are referred
                by you, you won’t receive rewards if they book with one another.
              </li>
              <li>
                If you refer a driver: the reward will be issued to your
                JustPark balance within 14 days of your referred user&apos;s
                first booking beginning.
              </li>
              <li>
                If you refer a space owner: the reward will be issued to your
                JustPark balance within 14 days of the referred user&apos;s
                first received booking beginning.
              </li>
              <li>
                Rewards issued to your JustPark balance can only be withdrawn to
                your bank account. You cannot use the rewards as credit on a
                booking.
              </li>
              <li>
                We reserve the right to close customers’ accounts of Referring
                or Referred Customers, if these have acquired rewards by fraud
                or have used or attempted to use the acquired rewards in a way
                that violates these terms of applicable laws
              </li>
              <li>
                We reserve the right to terminate the Programme or change the
                terms at its own discretion at any time
              </li>
              <li>
                Fraud or abuse relating to the accrual of rewards in the
                Programme may result in forfeiture of accrued rewards as well as
                cancellation of a User&apos;s Account and any JustPark Customer
                Account.
              </li>
              <li>
                Our failure to enforce a particular Term or Condition does not
                constitute a waiver of that Term or Condition by us.
              </li>
              <li>
                All questions or disputes regarding eligibility for the
                Programme or the eligibility of rewards for accrual will be
                resolved by JustPark in its sole discretion
              </li>
              <li>
                The Programme and any use of The Website are subject to the
                ‘terms and conditions’ of The Website and the privacy policy
                terms, as described on the Website
              </li>
            </OrderedList>
          </OrderedList>
          <h3 className={bem(policiesClass, 'sub-heading')}>
            Car Park Terms &amp; Conditions
          </h3>
          <p>
            The following terms and conditions relate to your use of our car
            parks.
          </p>
          <OrderedList alpha bold>
            <li>
              Our liability to you
              <OrderedList roman margin>
                <li>
                  We must operate the Car Park with reasonable skill and care
                  (“our obligations”). If we do not, we are only responsible for
                  direct loss or damage you suffer as a foreseeable result of
                  our breach of our obligations or our negligence or the
                  negligence of our employees.
                </li>
                <li>
                  We do not exclude or limit in any way our liability for death
                  or personal injury caused by our negligence or the negligence
                  of our employees, agents or subcontractors.
                </li>
                <li>
                  Although we owe you the obligations set out at clauses 1.1 and
                  1.2 above, you should be aware that the Car Park is open to
                  the general public. We cannot guarantee that members of the
                  general public will not enter our Car Park and cause damage to
                  property and/or engage in criminal activity. Accordingly, you
                  park your vehicle in the Car Park at your own risk. We cannot
                  and do not guarantee the security of your vehicle and/or its
                  contents.
                </li>
              </OrderedList>
            </li>
            <li>
              Tariff
              <OrderedList roman margin>
                <li>
                  The parking tariff payable by you (as varied from time to
                  time) will be displayed on our website, apps and phone payment
                  service. You are obliged to pay the parking tariff and to
                  comply with any instructions on the signage as supplemented by
                  these Terms. Failure to do so may result in us issuing you
                  with a Parking Charge Notice (please refer to clause 5
                  (Parking Contraventions) of these Terms).
                </li>
                <li>
                  Should you fail to make payment and have a payment method
                  stored with JustPark, JustPark are authorised to automatically
                  take payment for the full amount due to us for time parked.
                  The fee will be calculated according to duration parked which
                  is collected from the Automatic Number Plate Recognition
                  technology installed in car parks.
                </li>
              </OrderedList>
            </li>
            <li>
              Claims and complaints
              <OrderedList roman margin>
                <li>
                  If your vehicle sustains damage while in the Car Park, your
                  vehicle is stolen or any possessions are stolen from your
                  vehicle while it is in the Car Park you should:
                  <OrderedList alpha margin>
                    <li>
                      immediately inform JustPark by contacting our Customer
                      Services Department through our help centre.
                    </li>
                    <li>
                      in the case of theft, immediately inform the police; and
                    </li>
                    <li>notify your insurers promptly.</li>
                  </OrderedList>
                </li>
                <li>
                  Before submitting a claim, please refer to clause 1.2 (Our
                  liability to you) of these Car Park Terms, which set out the
                  extent of our responsibility to you in relation to any loss.
                </li>
              </OrderedList>
            </li>
            <li>
              Parking Contraventions
              <OrderedList roman margin>
                <li>
                  It is important for the effective management of the Car Park
                  that:
                  <OrderedList alpha margin>
                    <li>
                      you comply with all signs in the Car Park, including these
                      Terms and all signage;
                    </li>
                    <li>you park within the limits of a marked bay;</li>
                    <li>
                      you do not park within a bay designated for a specific
                      purpose when you are not entitled to do so (for example,
                      and without limitation, parking in a space designated for
                      disabled persons without an appropriate disability badge
                      displayed, and/or marked pre-bookable spaces); and
                    </li>
                  </OrderedList>
                </li>
                <li>
                  If you do not comply with these requirements we may issue you
                  with a Parking Charge Notice requiring you to pay any unpaid
                  parking charge(s), together with an additional amount
                  representing an estimate of the additional expenses we will
                  incur as a result of your non-compliance (including without
                  limitation debt recovery costs). Specific details about the
                  Parking Charge payable are available in each Car Park.
                </li>
                <li>
                  Details/information relating to how to pay the Parking Charge,
                  deadlines for payment, what will happen if you fail to make
                  payment within the stipulated deadline, and the appeal process
                  will be set out on the Parking Charge Notice.
                </li>
                <li>
                  By parking your vehicle in the Car Park you consent to us
                  capturing, using and processing your VRM and personal details
                  via CCTV and ANPR for enforcement purposes, to calculate the
                  relevant parking tariff (if applicable) and to recover any
                  outstanding Parking Charge. This includes our right to request
                  and obtain the details of a vehicle’s registered keeper from
                  the DVLA.
                </li>
                <li>
                  If the Car Park and/or the equipment in the Car Park is
                  damaged by you, your vehicle, its contents or the passengers
                  in the vehicle then, except where the damage arises as a
                  direct result of our negligence, we will seek to recover the
                  cost of that repair and associated administration costs from
                  you.
                </li>
              </OrderedList>
            </li>
            <li>
              Security of your vehicle
              <OrderedList roman margin>
                <li>
                  Please ensure that your vehicle is left securely locked with
                  all windows securely closed and any vehicle alarm, steering
                  lock or similar device fitted is engaged. We are not
                  responsible for any consequence or loss arising from a failure
                  by you to properly secure your vehicle.
                </li>
                <li>
                  We may install CCTV cameras in the Car Park at our discretion
                  to assist in its proper running. We acknowledge that the
                  cameras may act as a deterrent to criminal activity, but do
                  not make any representation as to the coverage provided or
                  guarantee of the security of your vehicle if CCTV is installed
                  in the Car Park.
                </li>
              </OrderedList>
            </li>
            <li>
              Possessions
              <OrderedList roman margin>
                <li>
                  Any possessions left in a vehicle are left entirely at the
                  owner’s risk. We suggest that no items are left so that they
                  are visible from the outside of the vehicle. We are not liable
                  for any theft by third parties from your vehicle.
                </li>
              </OrderedList>
            </li>
            <li>
              Safety in the Car Park
              <OrderedList roman margin>
                <li>
                  For safety reasons you are not entitled to remain in your
                  vehicle in the Car Park or elsewhere in the Car Park except
                  for the purposes of parking or removing your vehicle.
                </li>
              </OrderedList>
            </li>
            <li>
              Abandoned vehicles
              <OrderedList roman margin>
                <li>
                  We are entitled to regard as abandoned any vehicle left in the
                  Car Park for more than 28 days without prior notification.
                </li>
                <li>
                  We reserve the rights to engage and/or permit a lawful
                  authority to remove (or where we are unable to identify the
                  current legal registered keeper of the vehicle, to take steps
                  ourselves to remove) and to dispose of as waste or sell any
                  abandoned vehicle. Before proceeding with the disposal or sale
                  of abandoned vehicles we will:
                  <OrderedList alpha margin>
                    <li>
                      refer the matter to the appropriate authorities, which may
                      include the local police and the DVLA; and
                    </li>
                    <li>
                      affix a notice to the vehicle at least 7 days before the
                      date on which we propose to remove the vehicle stating
                      that the vehicle will be removed and sold when that period
                      expires.
                    </li>
                  </OrderedList>
                </li>
                <li>
                  Abandoned vehicles will be disposed of as waste or sold by
                  auction. Where sold by us, the proceeds of sale will be
                  applied in and towards satisfaction of all sums owing to us
                  together with the expenses of sale and our reasonable storage
                  and removal costs for the period during which the vehicle is
                  in our possession.
                </li>
                <li>
                  Any balance of the sale proceeds remaining after satisfaction
                  of any sums owing will be held by us on behalf of the
                  registered keeper of the vehicle and paid over on proof of
                  entitlement.
                </li>
              </OrderedList>
            </li>
            <li>
              Prohibited activities
              <OrderedList alpha margin>
                <li>
                  You must not tow any vehicle into the Car Park except as part
                  of the services offered at the Car Park by persons authorised
                  by us and no work on and no cleaning of vehicles by you or
                  your agent, other than with our prior specific permission, is
                  permitted in the Car Park. In the event of vehicle breakdown
                  you must contact JustPark to ensure that your vehicle removal
                  or repair is organised without causing disruption, damage or
                  danger to any other person or property in the Car Park.
                </li>
                <li>
                  No activity in connection with the selling, hiring or other
                  disposal of vehicles or goods or services may be carried out
                  in the Car Park without our prior specific written permission.
                </li>
                <li>
                  You are not allowed to dispose of any items or drop litter in
                  the Car Park. Please place any rubbish/litter in the bins that
                  are provided or take it away with you when you leave the Car
                  Park.
                </li>
                <li>
                  You are not allowed to pour petrol, or any other fuel, into
                  your vehicle whilst it is in the Car Park. You are also not
                  allowed to take petrol, or any other fuel, out of your vehicle
                  whilst it is in the Car Park.
                </li>
                <li>
                  Smoking is forbidden within both the Car Park and the
                  immediate vicinity of the Car Park.
                </li>
                <li>
                  Anti-social behaviour will not be accepted in our Car Parks.
                  This includes (but is not limited to) drinking alcohol, taking
                  or dealing with illegal substances, fighting and/or loitering.
                  Car parks are designed for the sole purpose of parking
                  vehicles. If an individual causes a nuisance or disturbance to
                  others it may be recorded by CCTV cameras and action may be
                  taken against them.
                </li>
              </OrderedList>
            </li>
          </OrderedList>

          <h2 className={bem(policiesClass, 'heading-h2')}>General</h2>
          <OrderedList number>
            <li>
              For users domiciled in the UK, these terms and conditions are
              governed by the laws of England of Wales and are made between
              ParkatmyHouse Ltd (trading as JustPark) and you.
            </li>

            <li>
              For users domiciled outside the UK, these terms and conditions are
              governed by the laws of the State of New York without reference to
              its principles of conflicts of laws and are made between JustPark
              (US) Inc and you.
            </li>
            <li>
              If you breach these terms and conditions and we decide to take no
              action or neglect to do so, then we will still be entitled to take
              action and enforce our rights and remedies for any other breach.
            </li>
            <li>
              We may make changes to the format of the Site, services provided
              or to the Site&apos;s content at any time without notice.
            </li>
          </OrderedList>
          <h3 className={bem(policiesClass, 'sub-heading')}>
            Mobile Payment Terms &amp; Conditions
          </h3>
          <p className={bem(policiesClass, 'sub-text')}>
            This agreement relates to the JustPark Parking Limited (“us”, “we”,
            “JustPark”) services we provide to our customers (“you”) to pay for
            parking quickly and efficiently using the JustPark application (the
            “App”) or IVR (interactive voice response) automated phone system or
            SMS service in selected parking facilities provided by parking
            operators or local government/authorities (the “Parking Providers”)
            to you (the “Services”).
          </p>
          <p>
            This agreement governs the relationship between us and you. When you
            use the Services we are providing you with the means to enter into
            individual, direct contracts with the Parking Providers to park in
            the Parking Providers’ facilities and spaces (“Parking Spaces”) and
            we facilitate the payment for your use of the Parking Space,
            provided by the Parking Providers. We do not have control over nor
            are we responsible for the Parking Providers, we simply facilitate
            the payment for your use of the Parking Space.
          </p>
          <OrderedList>
            <li>
              <h3 className={bem(policiesClass, 'heading-h3')}>
                Important points to note about the JustPark Services:
              </h3>
              <p className={bem(policiesClass, 'sub-text')}>
                The following are key terms applicable to your use of the
                Services. However, these terms are only highlighted to you for
                ease of reference, they are not a substitute for reading the
                full terms and conditions. Please scroll down to read the full
                terms and conditions and please make sure you fully understand
                them before signing up to use the Services. You should print a
                copy for your future reference.
              </p>
              <OrderedList roman margin>
                <li>
                  In order to use the Services you must create a JustPark
                  account by either downloading the App and registering,
                  registering through the JustPark website or using our
                  Interactive Voice Recognition (IVR) service. If you are
                  registering through the App, your account can be created by
                  signing in via your Google or Facebook account or through
                  using your email address and creating a password.
                </li>
                <li>
                  Once you have created your account it is your responsibility
                  to keep your account details, including your password, safe
                  and up to date and to not share these with anyone else.
                </li>
                <li>
                  Each time you use the Services to pay for a Parking Space
                  (each a “Parking Session”) your Parking Session will be
                  confirmed either in the App, via email, SMS or over the phone
                  depending on the method you have used to confirm your Parking
                  Session. Your Parking Session begins at the point payment is
                  taken. Your exact start time will be confirmed within your
                  confirmation. Please see below ‘how to use JustPark services’
                  for more information.
                </li>
                <li>
                  You can pay for a Parking Session using a credit or debit card
                  or via SMS. In addition, when you are using the App to pay, it
                  is possible to use Apple Pay or Android Pay in order to pay
                  for your use of the Services. You can enable this on your
                  mobile device. Whichever payment details you select when you
                  create your account will be used for payment each time you use
                  the Services. You can update the payment details you use to
                  pay for the Services before confirming each Parking Session.
                </li>
                <li>
                  These Services are of a payment services nature and therefore
                  the Services do not fall under the scope of the Consumer
                  Contracts (Information, Cancellation and Additional Payments)
                  Regulations 2013. You therefore commit to the transaction
                  through the App when you confirm a Parking Session.
                </li>
                <li>
                  During your Parking Session you will be subject to the terms
                  and restrictions shown on local signage and all road markings
                  in and around the Parking Space. It is your responsibility to
                  ensure you comply with all signage, road markings and any
                  relevant legislation that may be applicable when parking your
                  vehicle.
                </li>
                <li>
                  Once you have received a confirmation it is not possible to
                  cancel your Parking Session. If there is an issue with your
                  Parking Session (for example you have input the incorrect
                  vehicle details or other information) please contact our
                  customer service team by clicking{' '}
                  <strong>
                    <a
                      href="https://help.justpark.com/hc/en-us/requests/new"
                      target="_blank"
                      rel="noreferrer"
                    >
                      here
                    </a>
                  </strong>{' '}
                  .Our customer service team will happily update the incorrect
                  details for you if they are permitted to do so by the parking
                  provider, but we will not be able to cancel the Parking
                  Session. We will not be responsible for any parking fines or
                  other losses you incur due to you providing incorrect
                  information in relation to your Parking Session.
                </li>
                <li>
                  If you have any concerns, queries or complaints please contact
                  our customer services team by clicking{' '}
                  <strong>
                    <a
                      href="https://help.justpark.com/hc/en-us/requests/new"
                      target="_blank"
                      rel="noreferrer"
                    >
                      here
                    </a>
                  </strong>
                  .
                </li>
                <li>
                  We will use the details you give to us in order to provide the
                  Services to you. We may also send other messages and updates
                  to you about our Services where you have consented to this. To
                  learn more about how we use the information you provide please
                  read our privacy policy.
                </li>
                <p>
                  If you are unsure how to use the Services or the App please
                  see the following page on our website{' '}
                  <strong>
                    <a href="https://help.justpark.com/hc/en-us/categories/200424647-Drivers">
                      help.justpark.com/hc/en-us/categories/200424647-Drivers
                    </a>
                  </strong>{' '}
                  for help.
                </p>
              </OrderedList>
            </li>
            <li>
              <h3 className={bem(policiesClass, 'heading-h3')}>
                How does this agreement work?
              </h3>
              <OrderedList roman margin>
                <li>
                  By creating a JustPark account you agree to be bound by these
                  JustPark service terms and conditions together with the
                  Privacy Policy. These documents govern your use of the App,
                  the Services and your account and are applicable to your use
                  of the App and Services generally every Parking Session you
                  create.
                </li>
                <li>
                  When you use the Service we are providing you with the means
                  to enter into individual, direct contracts with the Parking
                  Providers to park in the Parking Spaces and we facilitate the
                  payment for your use of the Parking Space provided by the
                  Parking Providers. We are not a party to the contracts between
                  you and the Parking Providers and will not be liable to you or
                  any third party in relation to the Parking Space other than in
                  accordance with these terms.
                </li>
                <li>
                  These terms and conditions may be updated from time to time as
                  we continually develop the services we provide. We may
                  therefore amend this agreement by posting the amended terms
                  and conditions on our website and in the App. These amended
                  terms and conditions would take effect 7 days following being
                  posted. Should you no longer wish to use the Services you can
                  simply stop using the App and Services. You can also log out
                  from your account on the App and can contact us by clicking{' '}
                  <strong>
                    <a
                      href="https://help.justpark.com/hc/en-us/requests/new"
                      target="_blank"
                      rel="noreferrer"
                    >
                      here
                    </a>
                  </strong>{' '}
                  and request that we deactivate your account.
                </li>
              </OrderedList>
            </li>
            <li>
              <h3 className={bem(policiesClass, 'heading-h3')}>
                How to register and your promises to us
              </h3>
              <OrderedList roman margin>
                <li>
                  In order to order to use the Services you must download the
                  App and create a JustPark account. Your account can be created
                  by signing in via your Google or Facebook account or through
                  using your email address and creating a password.
                </li>
                <li>
                  By creating an account with us you confirm that:
                  <OrderedList alpha margin>
                    <li>
                      all information and details provided by you to us
                      (including on registration for an account) are true,
                      accurate and up to date in all respects and at all times.
                      You can update or correct your personal details at any
                      time through the account settings section of the App;
                    </li>
                    <li>
                      you only provide us with payment, credit or debit card
                      details for an account which is yours and which you are
                      authorised to use to make payments;
                    </li>
                    <li>
                      you will not do anything which may disrupt, damage, or
                      impair the Service (or any part thereof), or prevent other
                      users from using any part of the Service;
                    </li>
                    <li>
                      you will not intercept or modify the communications
                      between the App and our servers;
                    </li>
                    <li>
                      you will not deliberately exploit any bugs you find in the
                      App;
                    </li>
                    <li>
                      you will not use the App for any illegal or unlawful
                      purpose;
                    </li>
                    <li>
                      you will not distribute viruses or any other technologies
                      in the App that may harm us or the interests of any other
                      users of the Services, or otherwise interfere with or
                      disrupt our systems; and
                    </li>
                    <li>
                      you will not provide or otherwise make available the App
                      in whole or in part (including any object and source
                      code), in any form to any person without prior written
                      consent from us.
                    </li>
                  </OrderedList>
                </li>
              </OrderedList>
            </li>
            <li>
              <h3 className={bem(policiesClass, 'heading-h3')}>
                How do I use JustPark services?
              </h3>
              <OrderedList roman margin>
                <li>
                  Once you have logged into the App you can use the App to pay
                  for Parking Sessions. You will provide / confirm the following
                  information before starting a Parking Session: the location of
                  the Parking Space (location ID), the duration of parking time
                  and the vehicle being parked. You will then be asked to
                  confirm the payment amount to start a Parking Session.
                </li>
                <li>
                  To pay for a Parking Session you can use our App, our
                  Interactive Voice Recognition (IVR) service or our SMS / text
                  message payment systems. If you have any issues in paying for
                  a Parking Session, you can also contact our call centre to
                  complete your payment. The information required to complete
                  your Parking Session will be requested from you and (if you
                  request) a confirmation SMS will be sent to you.
                </li>
                <li>
                  If you wish to pay using our IVR or SMS payment systems please
                  follow the instructions available here that are also displayed
                  on signs around the Parking Space.
                </li>
                <li>
                  Parking Session will be confirmed either in the App, via
                  email, SMS or over the phone depending on the method you have
                  used to confirm your Parking Session. Your Parking Session
                  begins at the point payment is taken. Your exact start time
                  will be confirmed within your confirmation.
                </li>
                <li>
                  Each Parking Session can only be used for the vehicle stated
                  in the confirmation.
                </li>
                <li>
                  It is your responsibility to provide us with the correct
                  details when using any of our payment methods to pay for a
                  Parking Session. We will under no circumstances be liable for
                  any parking fines or other losses you incur due to you
                  providing incorrect data through any of the payment systems
                  (being the App, the IVR, SMS service or our call centre) in
                  relation to your Parking Session. It is your responsibility to
                  ensure a Parking Session has been created and contains the
                  correct details for the Parking Session.
                </li>
                <li>
                  You acknowledge you will not be able to access and use certain
                  functionalities of the App unless you have internet access
                  through a GPRS, 4G, 3G or Wi-Fi connected mobile device. All
                  traffic charges or access charges incurred due to the use of
                  the App are subject to your agreed terms with your mobile
                  network provider.
                </li>
                <li>
                  Should you choose to use our SMS service we cannot provide any
                  guarantee that we will be able to process your request and
                  start your Parking Session within a certain time of the SMS
                  being sent. We will process your request from the point at
                  which we receive the message from your mobile network.
                </li>
                <li>
                  During your Parking Session you will be subject to the terms
                  and restrictions shown on local signage and all road markings
                  in and around the Parking Space. It is your responsibility to
                  ensure you comply with all signage, road markings and any
                  relevant legislation in relation to parking of your vehicle
                  that may be applicable.
                </li>
                <li>
                  If you have enabled push notifications on the App, have
                  provided us with permission to access your location data, and
                  if you have internet access to your connected mobile device,
                  then while your Parking Session is active, we will detect
                  where you are located in relation to your vehicle in order to
                  advise when you may want to start returning to your vehicle to
                  ensure you return within your allotted time.
                </li>
                <li>
                  If you wish to extend your Parking Session you can do so in
                  the App.
                </li>
                <li>
                  You can also view your Parking Session history by looking in
                  the bookings section of the App. Here you can see the
                  location, vehicle parked, duration and cost of the Parking
                  Session and a link to a downloadable receipt is also provided.
                </li>
                <li>
                  Should you use any electric vehicle charging points or other
                  services provided by the Parking Provider during your Parking
                  Session you must follow all user instructions when doing so.
                  It is your responsibility to ensure that you use these
                  additional services safely so as to avoid injury to any person
                  or damage to property, in particular in relation to charging
                  points you must ensure you have the correct connector cables,
                  that the cables are safely plugged in and do not create a
                  tripping hazard and that the charging cables are removed
                  before moving the vehicle.
                </li>
              </OrderedList>
            </li>
            <li>
              <h3 className={bem(policiesClass, 'heading-h3')}>
                How will I be charged?
              </h3>
              <OrderedList roman margin>
                <li>
                  You will be charged using the payment information you provide
                  when you set up your account and that you confirm you wish to
                  use before starting each Parking Session.
                </li>
                <li>
                  You will be charged the amount shown on screen or confirmed to
                  you over the phone or via SMS message before your Parking
                  Session begins.
                </li>
                <li>
                  You can update the payment details you use to pay for the
                  Services before confirming each Parking Session.
                </li>
                <li>
                  You will be liable for any charges in connection to any calls
                  made and SMS/ text services used when paying for your Parking
                  Session. You may be asked if you want to opt-in to receive
                  text message reminders or confirmations/receipts. These
                  messages may be chargeable and you will be made aware of the
                  fee before paying.
                </li>
                <li>
                  Your payments for Parking Sessions will be processed by our
                  trusted payment partner.
                </li>
                <li>
                  Once you have received a confirmation it is not possible to
                  cancel your Parking Session. If there is an issue with your
                  Parking Session (for example you have input the incorrect
                  vehicle details or other information) please contact our
                  customer service team by clicking{' '}
                  <strong>
                    <a
                      href="https://help.justpark.com/hc/en-us/requests/new"
                      target="_blank"
                      rel="noreferrer"
                    >
                      here
                    </a>
                  </strong>
                  . Our customer service team will happily update the incorrect
                  details for you, but we will not be able to cancel the Parking
                  Session. We will not be responsible for any parking fines or
                  other losses you incur due to you providing incorrect
                  information in relation to your Parking Session.
                </li>
              </OrderedList>
            </li>
            <li>
              <h3 className={bem(policiesClass, 'heading-h3')}>
                The availability of the JustPark services and Parking Spaces
              </h3>
              <OrderedList roman margin>
                <li>
                  Whilst we use reasonable efforts to provide a reliable service
                  and software, we do not and cannot guarantee that the JustPark
                  Services will be available 100% of the time.
                </li>
                <li>
                  JustPark also do not guarantee the availability or adequacy of
                  Parking Spaces or other services provided by Parking Providers
                  or third parties during Parking Sessions.
                </li>
              </OrderedList>
            </li>
            <li>
              <h3 className={bem(policiesClass, 'heading-h3')}>
                Your information and data
              </h3>
              <OrderedList roman margin>
                <li>
                  The data you provide to us must be accurate and you must have
                  the right to provide us with such data.
                </li>
                <li>
                  Protecting your privacy is important to us, please review our
                  privacy policy here in order to better understand our
                  commitment to maintaining your privacy as well as our use and
                  disclosure of your information.
                </li>
                <li>
                  You agree to us using your personal information as described
                  in our privacy policy and sharing certain information about
                  you where necessary with third parties as set out in our
                  privacy policy including, for example, with payment processors
                  and enforcement officers operating during your Parking
                  Sessions.
                </li>
              </OrderedList>
            </li>
            <li>
              <h3 className={bem(policiesClass, 'heading-h3')}>
                Your security when using the services
              </h3>
              <OrderedList roman margin>
                <li>
                  You must not share your password and account details with
                  anyone else.
                </li>
                <li>
                  You must let us know if you think there has been unauthorised
                  use of your account and / or if your phone or card is stolen
                  or goes missing. We will not be liable for others using your
                  account or card details to generate Parking Sessions if you
                  have allowed other people to gain access to your account.
                </li>
              </OrderedList>
            </li>
            <li>
              <h3 className={bem(policiesClass, 'heading-h3')}>
                When we can suspend your use of the services and / or close your
                account
              </h3>
              <OrderedList roman margin>
                <li>
                  You acknowledge that your use of the JustPark Services is
                  subject to our discretion and we may, in the event of your
                  breach of these terms, at our sole discretion, suspend your
                  account or withdraw your rights to use the JustPark Service on
                  written notice with immediate effect.
                </li>
                <li>
                  Circumstances in which we may suspend or withdraw your account
                  include but are not limited to, our reasonable belief that you
                  have used an account for payment which you are not entitled to
                  use, or you have breached these terms and conditions.
                </li>
              </OrderedList>
            </li>
            <li>
              <h3 className={bem(policiesClass, 'heading-h3')}>
                Your liability under the agreement
              </h3>
              <OrderedList roman margin>
                <li>
                  You are liable for paying all of the charges in relation to
                  your use of the Services and the App.
                </li>
                <li>
                  You are also liable to pay any fines and / or penalty notices
                  you receive during your use of the Services. We will under no
                  circumstances be liable to you for any loss or damage you may
                  suffer in relation to any parking fines applied by authorities
                  to you in relation to your breach of any Parking Partners
                  rules and regulations, road markings or other relevant
                  legislation.
                </li>
              </OrderedList>
            </li>
            <li>
              <h3 className={bem(policiesClass, 'heading-h3')}>
                Our liability to you under this agreement:
              </h3>
              <OrderedList roman margin>
                <li>
                  We accept liability for death or personal injury caused by our
                  negligence. We also accept liability for fraud and fraudulent
                  misrepresentation by us.
                </li>
                <li>
                  Other than our liability mentioned above, if we breach these
                  terms or are otherwise liable to you for any other type of
                  claim, we shall only be liable to you for losses up to the
                  greater of: a) the amount paid by you to us in relation to the
                  Parking Session giving rise to the dispute between us or b)
                  £100.
                </li>
                <li>
                  We are not responsible for: (i) losses not caused by our
                  breach; (ii) indirect losses (which means loss to you which is
                  a side effect of the main loss or damage, and where you and we
                  could not have reasonably expected that loss would arise at
                  the time of entering into these terms); (iii) any loss arising
                  as a result of the accuracy, timeliness, completeness or
                  usefulness of any information; or (iv) failure to provide the
                  Services or to meet any of our obligations under these terms
                  where such failure is due to events beyond our control (for
                  example a network failure).
                </li>
                <li>
                  You have certain rights under the law. Nothing in these terms
                  (including this section) is intended to or will affect these
                  statutory rights.
                </li>
              </OrderedList>
            </li>
            <h3 className={bem(policiesClass, 'heading-h3')}>
              12. Resolving disputes:
            </h3>
            <OrderedList roman margin>
              <li>
                If you have any questions, complaints or comments about us or
                the services then please contact us via help.justpark.com, or in
                writing by post to: JustPark, Dunn&apos;s Hat Factory, 106 - 110
                Kentish Town Road, London, NW1 9PX.
              </li>
              <li>
                If you breach these terms and we take no action against you, we
                will still be entitled to use our rights and remedies in any
                other situation where you breach these terms.
              </li>
              <li>
                Please contact the Parking Provider if your query or complaint
                relates to an issue with a Parking Space itself.
              </li>
            </OrderedList>
            <h3 className={bem(policiesClass, 'heading-h3')}>
              13. General provisions:
            </h3>
            <OrderedList roman margin>
              <li>
                These terms are not intended to give rights to anyone except you
                and us.
              </li>
              <li>
                You acknowledge that we may transfer our rights under the terms
                (and any related claims) to any third party without having to
                obtain your prior permission.
              </li>
              <li>
                If any part of these terms is disallowed or found to be
                ineffective by a court or regulator, the other provisions shall
                continue to apply.
              </li>
              <li>
                These terms together with our policies contain all terms agreed
                between you and us. They replace any prior agreements between
                you and us in relation to the Services.
              </li>
              <li>
                You agree that your right to access and use the Services is also
                subject to applicable law and any rules or policies applied by
                the relevant App Store from which you access the App.
              </li>
            </OrderedList>
            <h3 className={bem(policiesClass, 'heading-h3')}>
              14. Governing law and jurisdiction
            </h3>
            <OrderedList roman margin>
              <li>
                These terms are governed by and interpreted in accordance with
                English law. We both agree to submit to the non-exclusive
                jurisdiction of the English courts, which means: (i) that if you
                live in the EU, you may enforce your rights in connection with
                these terms in the EU member state in which you live; or (ii) if
                you live in any other jurisdiction which gives you mandatory
                consumer protection rights, you may enforce your rights in
                connection with the terms in accordance with such rights.
              </li>
            </OrderedList>
          </OrderedList>

          <h2 className={bem(policiesClass, 'heading-h2')}>
            Further questions
          </h2>
          <p>
            If at any time you would like to contact us with your views about
            our terms of use, you can do so by clicking{' '}
            <strong>
              <a
                href="https://help.justpark.com/hc/en-us/requests/new"
                target="_blank"
                rel="noreferrer"
              >
                here
              </a>
            </strong>
            .
          </p>
        </>
      )}
    </PoliciesWrapper>
  );
};

export default TermsAndConditions;
